'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect } from 'react';
interface UseClickOutsideProps {
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
}

/**
 * Elements marked with this attribute (or containing an element marked with it)
 * are ignored by useClickOutside, even when they render outside containerRef —
 * e.g. toasts portaled to document.body that should not dismiss an open modal.
 */
export const CLICK_OUTSIDE_IGNORE_ATTR = 'data-click-outside-ignore';

/**
 * Detects click events that occur outside of the given container element.
 * Useful for overlays such as drawers, modals, or popovers.
 *
 * Uses pointerdown to track drag origin + click for dismissal — so drag-release
 * outside the container does not trigger onClickOutside. The browser also
 * natively suppresses click after a scroll gesture.
 */
export const useClickOutside = t0 => {
  const $ = _c(5);
  const {
    containerRef,
    enabled,
    onClickOutside
  } = t0;
  let t1;
  let t2;
  if ($[0] !== containerRef || $[1] !== enabled || $[2] !== onClickOutside) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      let pressedInside = false;
      const handlePointerDown = event => {
        const container = containerRef.current;
        const target = event.target;
        pressedInside = target instanceof Node && !!container && container.contains(target);
      };
      const handleClick = event_0 => {
        if (pressedInside || event_0.defaultPrevented) {
          return;
        }
        const container_0 = containerRef.current;
        const target_0 = event_0.target;
        if (!(target_0 instanceof Node)) {
          return;
        }
        if (!container_0 || container_0.contains(target_0)) {
          return;
        }
        if (target_0 instanceof Element && target_0.closest(`[${CLICK_OUTSIDE_IGNORE_ATTR}]`)) {
          return;
        }
        onClickOutside?.(event_0);
      };
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("click", handleClick, true);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("click", handleClick, true);
      };
    };
    t2 = [containerRef, enabled, onClickOutside];
    $[0] = containerRef;
    $[1] = enabled;
    $[2] = onClickOutside;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};